import { SIGN_IN_ERROR_PARAM, type SignInError } from '@repo/contracts';

import { APP_ROUTES } from '@/constants/app-routes';
import { writeSignedOut } from '@/shared/utils/signed-out-flag';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

// Several requests can fail at once, and a screen's own guard can race the
// interceptor. Every redirect is a full page load, which resets this.
let redirecting = false;

/** Takes the one redirect. False means another is already under way. */
export const claimRedirect = (): boolean => {
  if (redirecting) return false;

  redirecting = true;
  return true;
};

/** A forced redirect leaves the page without warning, so say why in the console. */
const warn = (reason: string): void => console.warn(`[auth] leaving for sign-in: ${reason}`);

/** Where the person was going, carried through the sign-in page so an emailed link still lands. */
const NEXT_PARAM = 'next';

/** The page to return to after sign-in: a path on this origin, never another site. */
export const readNextPath = (): string | null => {
  const next = new URLSearchParams(window.location.search).get(NEXT_PARAM);
  const isLocalPath = next?.startsWith('/') && !next.startsWith('//') && !next.startsWith('/\\');

  return isLocalPath && next ? next : null;
};

/** Goes to this app's own sign-in page, optionally explaining why. */
export const redirectToLogin = (reason: string, error?: SignInError): void => {
  if (!claimRedirect()) return;

  warn(reason);
  const query = new URLSearchParams();
  if (error) query.set(SIGN_IN_ERROR_PARAM, error);

  const { pathname, search, hash } = window.location;
  if (pathname !== APP_ROUTES.login && pathname !== '/') {
    query.set(NEXT_PARAM, `${pathname}${search}${hash}`);
  }

  const queryString = query.toString();
  window.location.assign(`${APP_ROUTES.login}${queryString ? `?${queryString}` : ''}`);
};

/**
 * A forced redirect: the session is gone, or there never was one. It always
 * stops at this app's own sign-in page and never goes on to the identity
 * provider by itself. An address that silently forwards to a sign-in form on
 * another host, with a Microsoft button on it, is what phishing scanners flag,
 * and the sign-in host was listed for exactly that. Leaving for the identity
 * provider is the person's own click, in startSignIn.
 */
export const redirectToSignIn = (reason: string): void => {
  // Already there: a request failing on the sign-in page needs no redirect.
  if (window.location.pathname === APP_ROUTES.login) return;

  redirectToLogin(reason);
};

const goToIdentityProvider = (): void => {
  let target = new URL(window.location.href);

  // A successful sign-in should not return to the public login screen, but to
  // the page the person was sent here from.
  if (target.pathname === APP_ROUTES.login) {
    target = new URL(readNextPath() ?? '/', window.location.origin);
  }

  const redirect = encodeURIComponent(target.toString());
  window.location.assign(`${API_BASE_URL}/auth/login?redirect=${redirect}`);
};

/** A person's own choice to sign in: the one thing that clears the "signed out" flag. */
export const startSignIn = (): void => {
  writeSignedOut(false);

  if (claimRedirect()) goToIdentityProvider();
};
